import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { readableTextColor, labelFontSize, labelFontWeight, resolvedSegmentPercentage, wrapLabel } from "../lib/chart_colors.js"

// Opens a presentation-ready chart preview modal with copy-to-clipboard.
// Supports: doughnut, pie, bar, column, text -- with both percentage and distribution data.

export default class extends Controller {
  static values = {
    percentage:      { type: Number, default: 0 },
    primaryColor:    { type: String, default: '#003d68' },
    secondaryColor:  { type: String, default: '#e5e7eb' },
    chartKind:       { type: String, default: 'doughnut' },
    labelStyle:      { type: String, default: 'percentage' },
    totalCount:      { type: Number, default: 0 },
    compliantCount:  { type: Number, default: 0 },
    title:           { type: String, default: 'Result' },
    displayText:     { type: String, default: '' },
    labels:          { type: Array, default: [] },
    data:            { type: Array, default: [] },
    highlightValues: { type: String, default: '' },
    colors:          { type: Array, default: [] },
    denominator:     { type: Number, default: 0 },
    labelColor:      { type: String, default: '#003d68' },
    labelSize:       { type: String, default: 'medium' },
    labelWeight:     { type: String, default: 'bold' },
    progress:        { type: Boolean, default: false }
  }

  get isDistribution() {
    return this.labelsValue.length > 0
  }

  open(event) {
    if (event) event.stopPropagation()
    if (this.dialog) return

    this.dialog = this.buildDialog()
    document.body.appendChild(this.dialog)

    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close()
    })

    this.dialog.showModal()

    const kind = this.chartKindValue
    if (kind === 'stat') {
      // Stat card is HTML-only, no canvas chart
    } else if (kind === 'gauge') {
      this.chart = this.renderGaugeChart(this.dialog.querySelector('[data-chart-canvas]'))
    } else {
      const canvas = this.dialog.querySelector('[data-chart-canvas]')
      if (canvas) {
        if (kind === 'bar' || kind === 'column') {
          this.chart = this.renderBarChart(canvas)
        } else if (kind === 'doughnut' || kind === 'pie') {
          this.chart = this.isDistribution ? this.renderDistributionChart(canvas) : this.renderPercentageChart(canvas)
        }
      }
    }
  }

  close() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
    if (this.dialog) { this.dialog.remove(); this.dialog = null }
  }

  async copy() {
    // Capture the entire snippable area (title + chart) as a single image
    const snippable = this.dialog?.querySelector('[data-snippable]')
    if (!snippable) return

    const button = this.dialog.querySelector('[data-copy-button]')

    try {
      // Use html2canvas-style approach: render to an offscreen canvas
      const canvas = await this.compositeToCanvas(snippable)
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])

      if (button) {
        const original = button.innerHTML
        button.innerHTML = '<span>Copied!</span>'
        button.classList.add('bg-green-600')
        setTimeout(() => { button.innerHTML = original; button.classList.remove('bg-green-600') }, 2000)
      }
    } catch (err) {
      // Fallback: try just the chart canvas
      const chartCanvas = this.dialog?.querySelector('[data-chart-canvas]')
      if (chartCanvas) {
        try {
          const blob = await new Promise(resolve => chartCanvas.toBlob(resolve, 'image/png'))
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          if (button) {
            const original = button.innerHTML
            button.innerHTML = '<span>Copied chart!</span>'
            button.classList.add('bg-green-600')
            setTimeout(() => { button.innerHTML = original; button.classList.remove('bg-green-600') }, 2000)
          }
        } catch (e) {
          const url = chartCanvas.toDataURL('image/png')
          window.open(url, '_blank')
        }
      }
    }
  }

  async compositeToCanvas(element) {
    const chartCanvas = element.querySelector('[data-chart-canvas]')

    if (chartCanvas) {
      // Canvas-based chart: composite title + chart onto a new canvas
      const titleEl = element.querySelector('[data-chart-title]')
      const titleVisible = titleEl && titleEl.style.display !== 'none'

      const padding = 32
      const titleHeight = titleVisible ? 48 : 0
      const chartWidth = chartCanvas.width || 600
      const chartHeight = chartCanvas.height || 400
      const totalWidth = chartWidth + (padding * 2)
      const totalHeight = chartHeight + titleHeight + (padding * 2)

      const composite = document.createElement('canvas')
      composite.width = totalWidth
      composite.height = totalHeight
      const ctx = composite.getContext('2d')

      const radius = 12
      ctx.beginPath()
      ctx.roundRect(0, 0, totalWidth, totalHeight, radius)
      ctx.fillStyle = '#ffffff'
      ctx.fill()
      ctx.strokeStyle = '#e5e7eb'
      ctx.lineWidth = 1
      ctx.stroke()
      ctx.clip()

      if (titleVisible) {
        const titleStyle = titleEl.querySelector('h3')?.style
        ctx.fillStyle = '#111827'
        ctx.font = `bold ${this._titleFontSize || 20}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
        ctx.textAlign = titleStyle?.textAlign || 'left'
        const x = ctx.textAlign === 'center' ? totalWidth / 2 : padding
        ctx.fillText(this.titleValue, x, padding + 24)
        ctx.textAlign = 'left'
      }

      ctx.drawImage(chartCanvas, padding, titleHeight + padding)
      return composite
    }

    // HTML-only chart (stat, table, text): screenshot via foreignObject SVG
    return await this.htmlToCanvas(element)
  }

  async htmlToCanvas(element) {
    const rect = element.getBoundingClientRect()
    const width = Math.round(rect.width)
    const height = Math.round(rect.height)

    // Clone the element and inline all computed styles
    const clone = element.cloneNode(true)
    this.inlineStyles(element, clone)

    const svgNs = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(svgNs, 'svg')
    svg.setAttribute('width', width)
    svg.setAttribute('height', height)

    const fo = document.createElementNS(svgNs, 'foreignObject')
    fo.setAttribute('width', '100%')
    fo.setAttribute('height', '100%')

    const body = document.createElement('div')
    body.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml')
    body.style.width = `${width}px`
    body.style.height = `${height}px`
    body.style.background = '#ffffff'
    body.appendChild(clone)
    fo.appendChild(body)
    svg.appendChild(fo)

    const svgData = new XMLSerializer().serializeToString(svg)
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = width * 2  // 2x for retina
        canvas.height = height * 2
        const ctx = canvas.getContext('2d')
        ctx.scale(2, 2)
        ctx.drawImage(img, 0, 0)
        URL.revokeObjectURL(url)
        resolve(canvas)
      }
      img.onerror = reject
      img.src = url
    })
  }

  inlineStyles(source, target) {
    const sourceChildren = source.children
    const targetChildren = target.children
    const computed = window.getComputedStyle(source)

    // Copy key styles
    const props = ['color', 'background-color', 'background', 'font-size', 'font-weight',
      'font-family', 'text-align', 'padding', 'margin', 'border', 'border-radius',
      'display', 'flex-direction', 'align-items', 'justify-content', 'gap',
      'width', 'height', 'max-width', 'line-height', 'letter-spacing', 'text-transform']
    props.forEach(prop => {
      target.style[prop] = computed.getPropertyValue(prop)
    })

    for (let i = 0; i < sourceChildren.length; i++) {
      if (targetChildren[i]) {
        this.inlineStyles(sourceChildren[i], targetChildren[i])
      }
    }
  }

  renderPercentageChart(canvas) {
    const ctx = canvas.getContext('2d')
    const isPie = this.chartKindValue === 'pie'
    // The arc clamps to a full ring; the percentage text beside it stays uncapped.
    // Without this, a result above 100% draws a negative remainder slice (ADR-0126).
    const arcPercentage = Math.min(Math.max(this.percentageValue, 0), 100)

    return new Chart(ctx, {
      type: isPie ? 'pie' : 'doughnut',
      data: {
        datasets: [{
          data: [arcPercentage, 100 - arcPercentage],
          backgroundColor: [this.primaryColorValue, this.secondaryColorValue],
          borderWidth: 0
        }]
      },
      options: {
        cutout: isPie ? '0%' : '65%',
        plugins: { legend: { display: false }, tooltip: { enabled: false }, datalabels: { display: false } },
        elements: { arc: { borderWidth: 0 } },
        responsive: true,
        maintainAspectRatio: false,
        animation: { animateRotate: true, duration: 600 }
      }
    })
  }

  renderDistributionChart(canvas) {
    const ctx = canvas.getContext('2d')
    const isPie = this.chartKindValue === 'pie'
    const labels = this._sortedLabels || this.labelsValue
    const data = this._sortedData || this.dataValue
    const baseColors = this.colorsValue.length === data.length ? this.colorsValue : this.buildColors(data.length)
    const colors = this.buildHighlightColors(labels, baseColors)

    return new Chart(ctx, {
      type: isPie ? 'pie' : 'doughnut',
      plugins: [ChartDataLabels],
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: colors,
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: isPie ? '0%' : '50%',
        plugins: {
          legend: this._legendPosition === 'hide'
            ? { display: false }
            : { position: this._legendPosition || 'right', labels: { boxWidth: 14, padding: 16, font: { size: this._labelFontSize || 14 } } },
          datalabels: {
            // The configured label colour wins here, same as the small-card
            // doughnut controller -- no per-segment contrast fallback, so it
            // doesn't fight a user's explicit colour choice (card #3613 round 12).
            color: this.labelColorValue,
            // Card #4273 round 2: this modal shared the small card's
            // centre-collision defect -- the datalabels plugin's default
            // 'center' anchor/align places every segment's label at its own
            // arc's midpoint, which collapses toward the ring centre for a
            // near-even multi-segment split (e.g. three checkbox options
            // around 33% each). Fixed the same way as
            // doughnut_chart_controller.js's renderDistribution: anchor
            // 'end' + align 'end' pushes each label out along its own
            // slice's radial line instead.
            //
            // Deliberately NOT adding layout.padding here, unlike the
            // small-card controller. The comment this replaced documented a
            // real regression from a *previous* round's attempt to add
            // padding on this canvas: it shrank the draw area and pulled a
            // symmetric 50/50 split's two opposite labels back toward the
            // centre until they collided. anchor/align alone don't shrink
            // anything -- they only change where within the existing draw
            // area each label's anchor point sits -- so a 50/50 split's two
            // labels (diametrically opposite before and after) stay
            // separated: both got pushed further toward their own side of
            // the ring, not pulled together. Verified against both cases
            // before committing: a 3-segment near-even split (the original
            // collision) now separates the labels around the ring instead of
            // stacking them near the centre, and a 50/50 split keeps its two
            // labels on opposite sides with MORE separation than before
            // (pushed outward, not inward) -- this does not reopen the
            // padding regression, because the fix this round doesn't touch
            // layout.padding at all.
            anchor: 'end',
            align: 'end',
            clamp: true,
            font: { weight: labelFontWeight(this.labelWeightValue), size: this._valueFontSize || labelFontSize(this.labelSizeValue) },
            formatter: (value, ctx) => {
              const total = ctx.dataset.data.reduce((a, b) => a + b, 0)
              const chartOwnPct = total > 0 ? Math.round(value / total * 100) : 0
              const style = this.labelStyleValue

              if (style === 'none') return ''
              if (style === 'count') return this.progressValue ? `${value} of ${this.denominatorValue}` : value
              if (style === 'both') return `${value} (${resolvedSegmentPercentage(value, total, this.denominatorValue)}%)`
              if (style === 'percentage_of_responses') {
                return `${resolvedSegmentPercentage(value, total, this.denominatorValue)}%`
              }
              // 'percentage' (default) and any unrecognised style
              return `${chartOwnPct}%`
            }
          }
        },
        animation: { animateRotate: true, duration: 600 }
      }
    })
  }

  renderBarChart(canvas) {
    const ctx = canvas.getContext('2d')
    const isHorizontal = this.chartKindValue === 'bar'
    const labels = this._sortedLabels || this.labelsValue
    const data = this._sortedData || this.dataValue
    const baseColors = this.colorsValue.length === data.length ? this.colorsValue : Array.from({ length: data.length }, () => this.primaryColorValue)
    const barColors = this.buildHighlightColors(labels, baseColors)
    const maxValue = Math.max(...data, 0)

    return new Chart(ctx, {
      type: 'bar',
      plugins: [ChartDataLabels],
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: barColors,
          borderWidth: 0
        }]
      },
      options: {
        indexAxis: isHorizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
          datalabels: {
            // Near-full-length bars would push an end-anchored label past the
            // chart's edge and clip it -- flip the label inside the bar (and
            // to a legible colour) once the bar is long enough to have room.
            color: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return maxValue > 0 && (value / maxValue) > 0.85 ? readableTextColor(barColors[context.dataIndex]) : this.labelColorValue
            },
            anchor: 'end',
            align: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return maxValue > 0 && (value / maxValue) > 0.85 ? 'start' : 'end'
            },
            clamp: true,
            font: { weight: labelFontWeight(this.labelWeightValue), size: this._valueFontSize || labelFontSize(this.labelSizeValue) },
            formatter: (value, context) => {
              if (value <= 0) return ''
              const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0)
              const chartOwnPct = total > 0 ? Math.round(value / total * 100) + '%' : ''
              const style = this.labelStyleValue
              if (style === 'none') return ''
              if (style === 'count') return this.progressValue ? `${value} of ${this.denominatorValue}` : value
              if (style === 'both') return `${value} (${resolvedSegmentPercentage(value, total, this.denominatorValue)}%)`
              if (style === 'percentage_of_responses') {
                return `${resolvedSegmentPercentage(value, total, this.denominatorValue)}%`
              }
              if (style === 'percentage') return chartOwnPct
              return value
            }
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            grid: { display: false },
            ticks: { precision: 0, font: { size: this._labelFontSize || 12 }, display: !isHorizontal },
            border: { display: !isHorizontal }
          },
          y: {
            beginAtZero: true,
            grid: { display: false },
            // Horizontal mode (isHorizontal) puts the category labels on this
            // axis -- wrap/truncate those the same way bar_chart_controller.js
            // does (card #4273 round 2). Vertical/column mode uses this axis
            // for numeric values, so the callback is left off there.
            ticks: isHorizontal
              ? { precision: 0, font: { size: this._labelFontSize || 12 }, callback: (value, index) => wrapLabel(labels[index]) }
              : { precision: 0, font: { size: this._labelFontSize || 12 } }
          }
        }
      }
    })
  }

  // Font weight/colour follow the configured label_color/label_weight
  // exactly. Size follows label_size as one step up/down from this dialog's
  // own defaults (48px/40px, matching the pre-round-12 hardcoded 3rem/2.5rem)
  // -- the same one-step-per-size idea as ChartConfigurable#chart_label_size_class,
  // expressed in raw px since this builder is JS template strings, not ERB
  // (card #3613 round 12).
  labelHtml() {
    const pct = Math.round(this.percentageValue)
    const count = this.compliantCountValue
    const total = this.totalCountValue
    const color = this.labelColorValue
    const weight = labelFontWeight(this.labelWeightValue)
    const style = this.labelStyleValue
    const bigPx = { small: 40, medium: 48, large: 56 }[this.labelSizeValue] ?? 48
    const bothPx = { small: 32, medium: 40, large: 48 }[this.labelSizeValue] ?? 40

    if (style === 'none') return ''
    const big = `style="color:${color}; font-size:${bigPx}px; font-weight:${weight}; line-height:1;"`
    const sub = `style="color:#9ca3af; font-size:0.875rem; margin-top:4px;"`

    if (style === 'count') return `<div style="text-align:center;"><div ${big}>${count}</div><div ${sub}>of ${total}</div></div>`
    if (style === 'both') return `<div style="text-align:center;"><div style="color:${color}; font-size:${bothPx}px; font-weight:${weight}; line-height:1;">${pct}%</div><div ${sub}>${count} / ${total}</div></div>`
    return `<div ${big}>${pct}%</div>`
  }

  static SIZES = [
    { name: 'Square', label: '1:1',  width: 480, height: 480 },
    { name: 'Small',  label: '4:3',  width: 480, height: 360 },
    { name: 'Medium', label: '16:9', width: 640, height: 360 },
    { name: 'Large',  label: '16:9', width: 960, height: 540 },
    { name: 'Wide',   label: '21:9', width: 960, height: 410 },
    { name: 'Tall',   label: '9:16', width: 480, height: 854 }
  ]

  buildDialog() {
    if (this.chartKindValue === 'text') return this.buildTextDialog()
    if (this.chartKindValue === 'stat') return this.buildStatDialog()
    if (this.chartKindValue === 'table') return this.buildTableDialog()

    const kind = this.chartKindValue
    const isDist = this.isDistribution
    const defaultSize = this.constructor.SIZES[2] // Medium 16:9

    const dialog = document.createElement('dialog')
    // A stable id so the embedded Settings form's Slim Selects can portal into
    // *this* dialog's top layer instead of the standalone tracking-settings
    // dialog (which isn't open). Only one chart-preview dialog can be open at
    // a time -- it's a native <dialog> modal, so the page behind it is inert.
    dialog.id = 'chart-preview-dialog'
    dialog.className = 'p-0 rounded-lg m-auto backdrop:bg-black/80'
    dialog.style.maxWidth = '95vw'
    dialog.style.width = 'max-content'

    const sizeTabs = this.constructor.SIZES.map((s, i) =>
      `<button type="button" data-size-index="${i}"
              class="px-2 py-1 text-xs font-medium rounded-md transition-colors ${i === 2 ? 'bg-sapphire text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}">${s.name}</button>`
    ).join('')

    // Track config state
    this._titleAlign = 'left'
    this._legendPosition = 'right'
    this._sortOrder = 'original'
    this._labelFontSize = 12
    this._valueFontSize = 14
    this._titleFontSize = 20
    this._hiddenSegments = new Set()
    // Store original order for restore
    this._originalLabels = [...this.labelsValue]
    this._originalData = [...this.dataValue]

    dialog.innerHTML = `
      <div class="bg-white rounded-lg flex flex-col" style="width: max-content;">
        <div style="display: grid; grid-template-columns: ${defaultSize.width}px 176px;" data-layout>
          <!-- Snippable area: title + chart (captured by copy) -->
          <div data-snippable class="border border-gray-200 rounded-l-lg overflow-hidden bg-white">
            <div data-chart-title class="px-6 pt-6 pb-2">
              <h3 class="text-xl font-semibold text-gray-900">${this.escapeHtml(this.titleValue)}</h3>
            </div>
            <div class="p-6 flex items-center justify-center" style="flex:1;">
              <div data-chart-container style="position:relative; width:100%; height:${defaultSize.height - 80}px; display:flex; align-items:center; justify-content:center;">
                <canvas data-chart-canvas></canvas>
                ${!isDist && kind !== 'bar' && kind !== 'column' ? `<div data-label-overlay style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none;">${this.labelHtml()}</div>` : ''}
              </div>
            </div>
          </div>

          <!-- Config sidebar (not captured in copy) -->
          <div class="border-l border-gray-200 bg-gray-50 flex flex-col rounded-tr-lg overflow-hidden" style="max-height: 600px;">
            <div data-sidebar-panel="config" class="p-3 flex flex-col gap-3 overflow-y-auto flex-1">
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title</label>
                <div class="flex rounded-md overflow-hidden border border-gray-200">
                  <button type="button" data-config="title-left"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white">Left</button>
                  <button type="button" data-config="title-centre"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Centre</button>
                  <button type="button" data-config="title-hide"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Hide</button>
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Sort</label>
                <select data-config-sort class="w-full text-xs border-gray-200 rounded-md py-1.5 pl-2 pr-6">
                  <option value="original">Question Order</option>
                  <option value="alpha-asc">A → Z</option>
                  <option value="alpha-desc">Z → A</option>
                  <option value="value-desc">High → Low</option>
                  <option value="value-asc">Low → High</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Legend</label>
                <div class="flex rounded-md overflow-hidden border border-gray-200">
                  <button type="button" data-config="legend-right"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white">Right</button>
                  <button type="button" data-config="legend-bottom"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Bottom</button>
                  <button type="button" data-config="legend-hide"
                          class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Hide</button>
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title Size</label>
                <input type="range" data-font="title" min="12" max="32" value="20" class="w-full h-1.5 accent-sapphire">
                <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>12</span><span data-font-label="title">20px</span><span>32</span></div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Label Size</label>
                <input type="range" data-font="label" min="8" max="20" value="12" class="w-full h-1.5 accent-sapphire">
                <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>8</span><span data-font-label="label">12px</span><span>20</span></div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Value Size</label>
                <input type="range" data-font="value" min="8" max="24" value="14" class="w-full h-1.5 accent-sapphire">
                <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>8</span><span data-font-label="value">14px</span><span>24</span></div>
              </div>
              ${isDist ? `
              <div data-segments-config>
                <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Segments</label>
                <div class="flex flex-col gap-1" data-segment-checkboxes>
                  ${this.labelsValue.map(label => `
                    <label class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input type="checkbox" checked data-segment-label="${this.escapeHtml(String(label))}"
                             class="rounded border-gray-300 text-sapphire focus:ring-sapphire">
                      <span>${this.escapeHtml(String(label))}</span>
                    </label>
                  `).join('')}
                </div>
              </div>
              ` : ''}
            </div>
          </div>
        </div>

        <!-- Controls (not captured in copy) -->
        <div class="border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
          <button type="button"
                  data-copy-button
                  class="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
          <div class="flex items-center gap-1.5" data-size-tabs>
            ${sizeTabs}
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <span data-size-label class="text-xs text-gray-400">${defaultSize.width}x${defaultSize.height}</span>
            <button type="button" data-close-button class="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    dialog.querySelector('[data-close-button]').addEventListener('click', () => this.close())
    dialog.querySelector('[data-copy-button]').addEventListener('click', () => this.copy())
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close() })

    // Size tab clicks
    dialog.querySelectorAll('[data-size-index]').forEach(btn => {
      btn.addEventListener('click', () => this.resizeChart(dialog, parseInt(btn.dataset.sizeIndex)))
    })

    // Config toggle clicks
    dialog.querySelectorAll('[data-config]').forEach(btn => {
      btn.addEventListener('click', () => this.applyConfig(dialog, btn.dataset.config))
    })

    // Sort select
    const sortSelect = dialog.querySelector('[data-config-sort]')
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this._sortOrder = sortSelect.value
        this.applySortAndRerender(dialog)
      })
    }

    // Font size sliders
    dialog.querySelectorAll('[data-font]').forEach(slider => {
      slider.addEventListener('input', () => {
        const type = slider.dataset.font
        const size = parseInt(slider.value)
        const label = dialog.querySelector(`[data-font-label="${type}"]`)
        if (label) label.textContent = `${size}px`

        if (type === 'title') {
          this._titleFontSize = size
          const titleEl = dialog.querySelector('[data-chart-title] h3')
          if (titleEl) titleEl.style.fontSize = `${size}px`
        } else if (type === 'label') {
          this._labelFontSize = size
          this.reRenderChart(dialog)
        } else if (type === 'value') {
          this._valueFontSize = size
          this.reRenderChart(dialog)
        }
      })
    })

    // Segment visibility checkboxes
    dialog.querySelectorAll('[data-segment-label]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const label = checkbox.dataset.segmentLabel
        if (checkbox.checked) {
          this._hiddenSegments.delete(label)
        } else {
          this._hiddenSegments.add(label)
        }
        this.applySegmentFilterAndRerender(dialog)
      })
    })

    return dialog
  }

  reRenderChart(dialog) {
    if (this.chart) { this.chart.destroy(); this.chart = null }
    const canvas = dialog.querySelector('[data-chart-canvas]')
    const kind = this.chartKindValue
    if (kind === 'bar' || kind === 'column') {
      this.chart = this.renderBarChart(canvas)
    } else if (kind === 'gauge') {
      this.chart = this.renderGaugeChart(canvas)
    } else if (kind === 'doughnut' || kind === 'pie') {
      this.chart = this.isDistribution ? this.renderDistributionChart(canvas) : this.renderPercentageChart(canvas)
    }
  }

  getSortedData() {
    const labels = [...this._originalLabels]
    const data = [...this._originalData]

    // Build pairs for sorting
    const pairs = labels.map((label, i) => ({ label, value: data[i] }))

    switch (this._sortOrder) {
      case 'alpha-asc':
        pairs.sort((a, b) => a.label.localeCompare(b.label))
        break
      case 'alpha-desc':
        pairs.sort((a, b) => b.label.localeCompare(a.label))
        break
      case 'value-desc':
        pairs.sort((a, b) => b.value - a.value)
        break
      case 'value-asc':
        pairs.sort((a, b) => a.value - b.value)
        break
      default: // 'original' -- keep as-is
        break
    }

    return {
      labels: pairs.map(p => p.label),
      data: pairs.map(p => p.value)
    }
  }

  applySortAndRerender(dialog) {
    this.applySegmentFilterAndRerender(dialog)
  }

  applySegmentFilterAndRerender(dialog) {
    const sorted = this.getSortedData()
    const filtered = sorted.labels.reduce((acc, label, i) => {
      if (!this._hiddenSegments.has(String(label))) {
        acc.labels.push(label)
        acc.data.push(sorted.data[i])
      }
      return acc
    }, { labels: [], data: [] })

    this._sortedLabels = filtered.labels
    this._sortedData = filtered.data

    if (this.chart) { this.chart.destroy(); this.chart = null }
    const canvas = dialog.querySelector('[data-chart-canvas]')
    const kind = this.chartKindValue
    if (kind === 'bar' || kind === 'column') {
      this.chart = this.renderBarChart(canvas)
    } else if (kind === 'doughnut' || kind === 'pie') {
      this.chart = this.isDistribution ? this.renderDistributionChart(canvas) : this.renderPercentageChart(canvas)
    }
  }

  applyConfig(dialog, config) {
    const titleEl = dialog.querySelector('[data-chart-title] h3')
    const configButtons = dialog.querySelectorAll('[data-config]')

    const activeClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white'
    const inactiveClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100'

    if (config.startsWith('title-')) {
      const titleContainer = dialog.querySelector('[data-chart-title]')

      if (config === 'title-hide') {
        this._titleAlign = 'hidden'
        if (titleContainer) titleContainer.style.display = 'none'
      } else {
        this._titleAlign = config === 'title-centre' ? 'center' : 'left'
        if (titleContainer) {
          titleContainer.style.display = ''
          titleEl.style.textAlign = this._titleAlign
        }
      }

      // Update toggle styling
      ;['title-left', 'title-centre', 'title-hide'].forEach(c => {
        const btn = dialog.querySelector(`[data-config="${c}"]`)
        if (btn) btn.className = (c === config) ? activeClass : inactiveClass
      })
    }

    if (config.startsWith('legend-')) {
      if (config === 'legend-hide') {
        this._legendPosition = 'hide'
      } else {
        this._legendPosition = config === 'legend-bottom' ? 'bottom' : 'right'
      }

      // Update toggle styling
      ;['legend-right', 'legend-bottom', 'legend-hide'].forEach(c => {
        const btn = dialog.querySelector(`[data-config="${c}"]`)
        if (btn) btn.className = (c === config) ? activeClass : inactiveClass
      })

      // Re-render chart with new legend setting
      if (this.chart) { this.chart.destroy(); this.chart = null }
      const canvas = dialog.querySelector('[data-chart-canvas]')
      const kind = this.chartKindValue
      if (kind === 'bar' || kind === 'column') {
        this.chart = this.renderBarChart(canvas)
      } else if (kind === 'gauge') {
        this.chart = this.renderGaugeChart(canvas)
      } else if (kind === 'doughnut' || kind === 'pie') {
        this.chart = this.isDistribution ? this.renderDistributionChart(canvas) : this.renderPercentageChart(canvas)
      }
    }
  }

  resizeChart(dialog, sizeIndex) {
    const size = this.constructor.SIZES[sizeIndex]
    const layout = dialog.querySelector('[data-layout]')
    const container = dialog.querySelector('[data-chart-container]')
    const sizeLabel = dialog.querySelector('[data-size-label]')

    // Update grid template to resize chart area
    if (layout) layout.style.gridTemplateColumns = `${size.width}px 176px`
    container.style.height = `${size.height - 80}px`
    if (sizeLabel) sizeLabel.textContent = `${size.width}x${size.height}`

    // Update active tab styling
    dialog.querySelectorAll('[data-size-index]').forEach((btn, i) => {
      if (i === sizeIndex) {
        btn.className = 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-sapphire text-white'
      } else {
        btn.className = 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200'
      }
    })

    // Destroy and re-render chart at new size
    if (this.chart) { this.chart.destroy(); this.chart = null }
    const canvas = dialog.querySelector('[data-chart-canvas]')
    const kind = this.chartKindValue
    if (kind === 'bar' || kind === 'column') {
      this.chart = this.renderBarChart(canvas)
    } else if (kind === 'gauge') {
      this.chart = this.renderGaugeChart(canvas)
    } else if (kind === 'doughnut' || kind === 'pie') {
      this.chart = this.isDistribution ? this.renderDistributionChart(canvas) : this.renderPercentageChart(canvas)
    }
  }

  // Card #3613 round 20 -- gauge is no longer a "distribution" kind (the view
  // stops passing labels/data for it), so this no longer recomputes a
  // dominant-answer percentage from those values. Mirrors
  // gauge_chart_controller.js's own initializeChart() exactly -- a single
  // compliant-vs-target arc driven by percentageValue, the same number the
  // label overlay (labelHtml()) already reads.
  renderGaugeChart(canvas) {
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    const pct = Math.min(Math.max(this.percentageValue, 0), 100)

    return new Chart(ctx, {
      type: 'doughnut',
      data: {
        datasets: [{
          data: [pct, 100 - pct],
          backgroundColor: [this.primaryColorValue, this.secondaryColorValue],
          borderWidth: 0
        }]
      },
      options: {
        circumference: 180,
        rotation: -90,
        cutout: '70%',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } }
      }
    })
  }

  buildStatDialog() {
    const data = this.dataValue
    const labels = this.labelsValue
    const total = data.reduce((a, b) => a + b, 0)
    const primary = this.primaryColorValue
    const labelColor = this.labelColorValue
    const weightClass = { normal: 'font-normal', semibold: 'font-semibold', bold: 'font-bold' }[this.labelWeightValue] ?? 'font-bold'
    // One step up/down from this dialog's own base sizes (text-6xl for the
    // Total headline, text-xl for Min/Avg/Max), same idea as
    // ChartConfigurable#chart_label_size_class but expressed as a lookup
    // table since this builder is JS template strings, not ERB.
    const totalSizeClass = { small: 'text-5xl', medium: 'text-6xl', large: 'text-7xl' }[this.labelSizeValue] ?? 'text-6xl'
    const minMaxSizeClass = { small: 'text-lg', medium: 'text-xl', large: 'text-2xl' }[this.labelSizeValue] ?? 'text-xl'

    const numericKeys = labels.map(Number).filter(n => !isNaN(n))
    const hasNumericKeys = numericKeys.length > 0

    let min, max, avg
    if (hasNumericKeys) {
      const pairs = labels.map((l, i) => ({ val: parseFloat(l), count: data[i] })).filter(p => !isNaN(p.val))
      const allValues = pairs.flatMap(p => Array(p.count).fill(p.val))
      min = Math.min(...allValues)
      max = Math.max(...allValues)
      avg = (allValues.reduce((a, b) => a + b, 0) / allValues.length).toFixed(1)
    } else {
      min = Math.min(...data)
      max = Math.max(...data)
      avg = (total / data.length).toFixed(1)
    }

    const defaultSize = this.constructor.SIZES[0]
    const sizeTabs = this.constructor.SIZES.map((s, i) =>
      `<button type="button" data-size-index="${i}"
              class="px-2 py-1 text-xs font-medium rounded-md transition-colors ${i === 0 ? 'bg-sapphire text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}">${s.name}</button>`
    ).join('')

    const dialog = document.createElement('dialog')
    dialog.className = 'p-0 rounded-lg m-auto backdrop:bg-black/80'
    dialog.style.maxWidth = '95vw'
    dialog.style.width = 'max-content'

    const bulletPct = max > min ? ((avg - min) / (max - min)) * 100 : 50

    dialog.innerHTML = `
      <div class="bg-white rounded-lg flex flex-col" style="width: max-content;">
        <div class="flex flex-nowrap">
          <div data-snippable style="width:${defaultSize.width}px;" class="shrink-0 border border-gray-200 rounded-l-lg overflow-hidden bg-white">
            <div data-chart-title class="px-6 pt-6 pb-2">
              <h3 class="text-xl font-semibold text-gray-900" style="text-align:center;">${this.escapeHtml(this.titleValue)}</h3>
            </div>
            <div class="px-8 pb-8 flex flex-col items-center gap-6" style="height:${defaultSize.height - 80}px; justify-content:center;">
              <div class="text-center">
                <div class="${totalSizeClass} ${weightClass} leading-none" style="color: ${labelColor};">${total.toLocaleString()}</div>
                <div class="text-sm text-gray-400 mt-2 uppercase tracking-wide">Total</div>
              </div>
              <div class="w-full max-w-xs">
                <div class="relative h-5 bg-gray-100 rounded-full overflow-hidden">
                  <div class="absolute inset-y-0 left-0 rounded-full" style="width: ${max > 0 ? (avg / max * 100) : 50}%; background: ${primary};"></div>
                  <div class="absolute top-0 bottom-0 w-1 rounded" style="left: ${bulletPct}%; background: #fcaf17;"></div>
                </div>
              </div>
              <div class="grid grid-cols-3 gap-3 w-full max-w-xs">
                <div class="bg-gray-50 rounded-lg p-3 text-center">
                  <div class="${minMaxSizeClass} ${weightClass} text-gray-900">${min}</div>
                  <div class="text-xs text-gray-400 uppercase">Min</div>
                </div>
                <div class="bg-gray-50 rounded-lg p-3 text-center">
                  <div class="${minMaxSizeClass} ${weightClass}" style="color: ${labelColor};">${avg}</div>
                  <div class="text-xs text-gray-400 uppercase">Avg</div>
                </div>
                <div class="bg-gray-50 rounded-lg p-3 text-center">
                  <div class="${minMaxSizeClass} ${weightClass} text-gray-900">${max}</div>
                  <div class="text-xs text-gray-400 uppercase">Max</div>
                </div>
              </div>
            </div>
          </div>
          <div class="w-44 shrink-0 border-l border-gray-200 bg-gray-50 p-3 flex flex-col gap-3 rounded-tr-lg">
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title</label>
              <div class="flex rounded-md overflow-hidden border border-gray-200">
                <button type="button" data-config="title-left" class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Left</button>
                <button type="button" data-config="title-centre" class="flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white">Centre</button>
                <button type="button" data-config="title-hide" class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Hide</button>
              </div>
            </div>
          </div>
        </div>
        <div class="border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
          <button type="button" data-copy-button
                  class="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
          <div class="flex items-center gap-1.5" data-size-tabs>${sizeTabs}</div>
          <div class="flex items-center gap-3 shrink-0">
            <span data-size-label class="text-xs text-gray-400">${defaultSize.width}x${defaultSize.height}</span>
            <button type="button" data-close-button class="text-gray-400 hover:text-gray-500" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    dialog.querySelector('[data-close-button]').addEventListener('click', () => this.close())
    dialog.querySelector('[data-copy-button]').addEventListener('click', () => this.copy())
    dialog.addEventListener('click', (e) => { if (e.target === dialog) this.close() })
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close() })

    // Size tabs
    dialog.querySelectorAll('[data-size-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.sizeIndex)
        const size = this.constructor.SIZES[idx]
        const snippable = dialog.querySelector('[data-snippable]')
        const content = snippable.querySelector('.px-8')
        snippable.style.width = `${size.width}px`
        if (content) content.style.height = `${size.height - 80}px`
        const sizeLabel = dialog.querySelector('[data-size-label]')
        if (sizeLabel) sizeLabel.textContent = `${size.width}x${size.height}`
        dialog.querySelectorAll('[data-size-index]').forEach((b, i) => {
          b.className = i === idx
            ? 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-sapphire text-white'
            : 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200'
        })
      })
    })

    // Config toggles
    const activeClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white'
    const inactiveClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100'
    dialog.querySelectorAll('[data-config]').forEach(btn => {
      btn.addEventListener('click', () => {
        const config = btn.dataset.config
        const titleEl = dialog.querySelector('[data-chart-title]')
        if (config === 'title-hide') {
          if (titleEl) titleEl.style.display = 'none'
        } else {
          if (titleEl) { titleEl.style.display = ''; titleEl.querySelector('h3').style.textAlign = config === 'title-centre' ? 'center' : 'left' }
        }
        ;['title-left', 'title-centre', 'title-hide'].forEach(c => {
          const b = dialog.querySelector(`[data-config="${c}"]`)
          if (b) b.className = (c === config) ? activeClass : inactiveClass
        })
      })
    })

    return dialog
  }

  buildTableDialog() {
    const data = this.dataValue
    const labels = this.labelsValue
    const total = data.reduce((a, b) => a + b, 0)
    const primary = this.primaryColorValue
    const pairs = labels.map((l, i) => ({ label: l, value: data[i] }))
    pairs.sort((a, b) => b.value - a.value)

    const defaultSize = this.constructor.SIZES[2] // Medium
    const sizeTabs = this.constructor.SIZES.map((s, i) =>
      `<button type="button" data-size-index="${i}"
              class="px-2 py-1 text-xs font-medium rounded-md transition-colors ${i === 2 ? 'bg-sapphire text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}">${s.name}</button>`
    ).join('')

    const rows = pairs.map(p => {
      const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0
      const fillPct = total > 0 ? (p.value / total) * 100 : 0
      return `
        <tr style="border-bottom: 1px solid #f3f4f6;">
          <td style="padding: 8px 12px; font-size: 14px; color: #111827;">${this.escapeHtml(p.label)}</td>
          <td style="padding: 8px 12px; width: 35%;">
            <div style="background: #f3f4f6; border-radius: 9999px; height: 12px; overflow: hidden;">
              <div style="height: 100%; border-radius: 9999px; width: ${fillPct}%; background: ${primary};"></div>
            </div>
          </td>
          <td style="padding: 8px 12px; font-size: 14px; font-weight: 600; color: #374151; text-align: right; white-space: nowrap;">${p.value}</td>
          <td style="padding: 8px 12px; font-size: 12px; color: #9ca3af; text-align: right; white-space: nowrap;">${pct}%</td>
        </tr>
      `
    }).join('')

    const dialog = document.createElement('dialog')
    dialog.className = 'p-0 rounded-lg m-auto backdrop:bg-black/80'
    dialog.style.maxWidth = '95vw'
    dialog.style.width = 'max-content'

    dialog.innerHTML = `
      <div class="bg-white rounded-lg flex flex-col" style="width: max-content;">
        <div class="flex flex-nowrap">
          <div data-snippable style="width:${defaultSize.width}px;" class="shrink-0 border border-gray-200 rounded-l-lg overflow-hidden bg-white">
            <div data-chart-title class="px-6 pt-6 pb-2">
              <h3 class="text-xl font-semibold text-gray-900">${this.escapeHtml(this.titleValue)}</h3>
            </div>
            <div class="px-6 pb-6" style="max-height:${defaultSize.height - 60}px; overflow-y:auto;">
              <table style="width:100%; border-collapse: collapse;">
                <thead>
                  <tr style="border-bottom: 2px solid #e5e7eb;">
                    <th style="padding: 8px 12px; text-align: left; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase;">Value</th>
                    <th style="padding: 8px 12px; width: 35%;"></th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase;">Count</th>
                    <th style="padding: 8px 12px; text-align: right; font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase;">%</th>
                  </tr>
                </thead>
                <tbody>${rows}</tbody>
              </table>
            </div>
          </div>
          <div class="w-44 shrink-0 border-l border-gray-200 bg-gray-50 p-3 flex flex-col gap-3 rounded-tr-lg">
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title</label>
              <div class="flex rounded-md overflow-hidden border border-gray-200">
                <button type="button" data-config="title-left" class="flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white">Left</button>
                <button type="button" data-config="title-centre" class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Centre</button>
                <button type="button" data-config="title-hide" class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Hide</button>
              </div>
            </div>
          </div>
        </div>
        <div class="border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
          <button type="button" data-copy-button
                  class="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
          <div class="flex items-center gap-1.5" data-size-tabs>${sizeTabs}</div>
          <div class="flex items-center gap-3 shrink-0">
            <span data-size-label class="text-xs text-gray-400">${defaultSize.width}x${defaultSize.height}</span>
            <button type="button" data-close-button class="text-gray-400 hover:text-gray-500" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    dialog.querySelector('[data-close-button]').addEventListener('click', () => this.close())
    dialog.querySelector('[data-copy-button]').addEventListener('click', () => this.copy())
    dialog.addEventListener('click', (e) => { if (e.target === dialog) this.close() })
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close() })

    // Size tabs
    dialog.querySelectorAll('[data-size-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.sizeIndex)
        const size = this.constructor.SIZES[idx]
        const snippable = dialog.querySelector('[data-snippable]')
        snippable.style.width = `${size.width}px`
        const tableArea = snippable.querySelector('.px-6.pb-6')
        if (tableArea) tableArea.style.maxHeight = `${size.height - 60}px`
        const sizeLabel = dialog.querySelector('[data-size-label]')
        if (sizeLabel) sizeLabel.textContent = `${size.width}x${size.height}`
        dialog.querySelectorAll('[data-size-index]').forEach((b, i) => {
          b.className = i === idx
            ? 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-sapphire text-white'
            : 'px-2 py-1 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200'
        })
      })
    })

    // Config toggles
    const activeClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white'
    const inactiveClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100'
    dialog.querySelectorAll('[data-config]').forEach(btn => {
      btn.addEventListener('click', () => {
        const config = btn.dataset.config
        const titleEl = dialog.querySelector('[data-chart-title]')
        if (config === 'title-hide') {
          if (titleEl) titleEl.style.display = 'none'
        } else {
          if (titleEl) { titleEl.style.display = ''; titleEl.querySelector('h3').style.textAlign = config === 'title-centre' ? 'center' : 'left' }
        }
        ;['title-left', 'title-centre', 'title-hide'].forEach(c => {
          const b = dialog.querySelector(`[data-config="${c}"]`)
          if (b) b.className = (c === config) ? activeClass : inactiveClass
        })
      })
    })

    return dialog
  }

  buildTextDialog() {
    this._titleAlign = 'center'
    // Compute display value: use explicit displayText, or total from data array
    const displayText = this.displayTextValue || (this.dataValue.length > 0 ? this.dataValue.reduce((a, b) => a + b, 0).toLocaleString() : '0')
    const defaultSize = this.constructor.SIZES[0] // Square
    // Font size/weight follow label_size/label_weight, one step per size
    // matching labelHtml()'s convention. Colour deliberately stays white --
    // this card's background gradient is the configured Primary colour, and
    // label_color defaults to Primary too, so swapping this text to
    // label_color would frequently paint it invisible against its own
    // background. This is the same kind of legibility-wins-over-configured-
    // colour call bar_chart_controller.js already makes for a near-full bar
    // (card #3613 round 12, my own call -- see the build report).
    const textValueSize = { small: 80, medium: 96, large: 112 }[this.labelSizeValue] ?? 96
    const textValueWeight = labelFontWeight(this.labelWeightValue)

    const sizeTabs = this.constructor.SIZES.map((s, i) =>
      `<button type="button" data-size-index="${i}"
              class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${i === 0 ? 'bg-sapphire text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}">${s.name} <span class="text-opacity-70">${s.label}</span></button>`
    ).join('')

    const dialog = document.createElement('dialog')
    dialog.className = 'p-0 rounded-lg m-auto backdrop:bg-black/80'
    dialog.style.maxWidth = '95vw'

    dialog.innerHTML = `
      <div class="bg-white rounded-lg flex flex-col">
        <div class="flex flex-nowrap">
          <div data-snippable style="width:${defaultSize.width}px;" class="shrink-0 border border-gray-200 rounded-lg overflow-hidden">
            <div style="height:${defaultSize.height}px; background: linear-gradient(135deg, ${this.primaryColorValue}ee, ${this.primaryColorValue});"
                 class="flex flex-col items-center justify-center p-8">
              <div data-chart-title class="mb-4">
                <h3 class="text-lg font-medium text-white/80 text-center">${this.escapeHtml(this.titleValue)}</h3>
              </div>
              <div data-text-value style="font-size:${textValueSize}px; font-weight:${textValueWeight}; line-height:1; text-align:center; color:#ffffff;">
                ${this.escapeHtml(displayText)}
              </div>
            </div>
          </div>

          <!-- Config sidebar -->
          <div class="w-48 shrink-0 border-l border-gray-200 bg-gray-50 p-4 flex flex-col gap-4 rounded-tr-lg">
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title</label>
              <div class="flex rounded-md overflow-hidden border border-gray-200">
                <button type="button" data-config="title-left"
                        class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Left</button>
                <button type="button" data-config="title-centre"
                        class="flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white">Centre</button>
                <button type="button" data-config="title-hide"
                        class="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100">Hide</button>
              </div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Value Size</label>
              <input type="range" data-font="text-value" min="32" max="128" value="${textValueSize}" class="w-full h-1.5 accent-sapphire">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>32</span><span data-font-label="text-value">${textValueSize}px</span><span>128</span></div>
            </div>
          </div>
        </div>

        <!-- Controls -->
        <div class="border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
          <button type="button"
                  data-copy-button
                  class="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
          <div class="flex items-center gap-1.5" data-size-tabs>
            ${sizeTabs}
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <span data-size-label class="text-xs text-gray-400">${defaultSize.width}x${defaultSize.height}</span>
            <button type="button" data-close-button class="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    dialog.querySelector('[data-close-button]').addEventListener('click', () => this.close())
    dialog.querySelector('[data-copy-button]').addEventListener('click', () => this.copy())
    dialog.addEventListener('click', (e) => { if (e.target === dialog) this.close() })
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close() })

    // Size tabs
    dialog.querySelectorAll('[data-size-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.sizeIndex)
        const size = this.constructor.SIZES[idx]
        const snippable = dialog.querySelector('[data-snippable]')
        const gradient = snippable.querySelector('div[style*="gradient"]')
        snippable.style.width = `${size.width}px`
        if (gradient) gradient.style.height = `${size.height}px`
        const sizeLabel = dialog.querySelector('[data-size-label]')
        if (sizeLabel) sizeLabel.textContent = `${size.width}x${size.height}`
        dialog.querySelectorAll('[data-size-index]').forEach((b, i) => {
          b.className = i === idx
            ? 'px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-sapphire text-white'
            : 'px-3 py-1.5 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200'
        })
      })
    })

    // Config toggles
    dialog.querySelectorAll('[data-config]').forEach(btn => {
      btn.addEventListener('click', () => {
        const config = btn.dataset.config
        const titleEl = dialog.querySelector('[data-chart-title]')
        const activeClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white'
        const inactiveClass = 'flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100'

        if (config === 'title-hide') {
          if (titleEl) titleEl.style.display = 'none'
        } else if (config === 'title-left' || config === 'title-centre') {
          if (titleEl) {
            titleEl.style.display = ''
            titleEl.querySelector('h3').style.textAlign = config === 'title-centre' ? 'center' : 'left'
          }
        }
        ;['title-left', 'title-centre', 'title-hide'].forEach(c => {
          const b = dialog.querySelector(`[data-config="${c}"]`)
          if (b) b.className = (c === config) ? activeClass : inactiveClass
        })
      })
    })

    // Value size slider
    dialog.querySelectorAll('[data-font]').forEach(slider => {
      slider.addEventListener('input', () => {
        const size = parseInt(slider.value)
        const label = dialog.querySelector(`[data-font-label="${slider.dataset.font}"]`)
        if (label) label.textContent = `${size}px`
        const valueEl = dialog.querySelector('[data-text-value]')
        if (valueEl) valueEl.style.fontSize = `${size}px`
      })
    })

    return dialog
  }

  buildColors(count) {
    const primary = this.primaryColorValue || '#003d68'
    if (count <= 2) return [primary, '#fcaf17']
    const blues = ['#003d68', '#0066b3', '#3399cc', '#66bbdd', '#99ddee', '#cce8f4']
    blues[0] = primary
    const extended = [...blues, '#fcaf17']
    return Array.from({ length: count }, (_, i) => extended[i % extended.length])
  }

  get highlightList() {
    const raw = this.highlightValuesValue
    if (!raw || raw.trim() === '') return []
    return raw.split(',').map(v => v.trim()).filter(v => v)
  }

  buildHighlightColors(labels, defaultColors) {
    const highlight = this.highlightList
    if (highlight.length === 0) return defaultColors
    return labels.map((label, i) => {
      if (highlight.includes(String(label))) return defaultColors[i]
      const hex = defaultColors[i] || '#003d68'
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r},${g},${b},0.25)`
    })
  }

  generateColors(count) {
    return this.buildColors(count)
  }

  escapeHtml(str) {
    return str.replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]))
  }
}
